import { useMemo, useState } from "react";
import {
  Bookmark,
  RotateCcw,
  Wrench,
  AlertTriangle,
  Sparkles,
  ChevronRight,
} from "lucide-react";

type DiagnosticCard = {
  id: string;
  problem: string;
  cause: string | string[];
  fix: string | string[];
  category?: string;
  severity?: string;
};

type FlipCardProps = {
  card: DiagnosticCard;
  cardNumber?: number;
  isBookmarked: boolean;
  onToggleBookmark: () => void;
};

function normalizeToArray(value: string | string[]) {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  return value
    .split(/,|•|\n|;/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getAccentClasses(problem: string) {
  const p = problem.toLowerCase();

  if (
    p.includes("white") ||
    p.includes("settling") ||
    p.includes("circulation")
  ) {
    return {
      border: "hover:border-slate-300/40",
      chip: "border-slate-300/20 bg-slate-300/10 text-slate-200",
      iconWrap: "border-slate-300/20 bg-slate-300/10",
      icon: "text-slate-200",
      glow: "hover:shadow-[0_0_24px_rgba(226,232,240,0.10)]",
      progress: "bg-slate-300",
    };
  }

  if (
    p.includes("powder") ||
    p.includes("peel") ||
    p.includes("bond") ||
    p.includes("lift") ||
    p.includes("wash")
  ) {
    return {
      border: "hover:border-emerald-300/40",
      chip: "border-emerald-300/20 bg-emerald-400/10 text-emerald-300",
      iconWrap: "border-emerald-300/20 bg-emerald-400/10",
      icon: "text-emerald-300",
      glow: "hover:shadow-[0_0_24px_rgba(52,211,153,0.10)]",
      progress: "bg-emerald-300",
    };
  }

  if (
    p.includes("head") ||
    p.includes("damper") ||
    p.includes("pump") ||
    p.includes("encoder") ||
    p.includes("carriage") ||
    p.includes("strike")
  ) {
    return {
      border: "hover:border-rose-300/40",
      chip: "border-rose-300/20 bg-rose-400/10 text-rose-300",
      iconWrap: "border-rose-300/20 bg-rose-400/10",
      icon: "text-rose-300",
      glow: "hover:shadow-[0_0_24px_rgba(251,113,133,0.10)]",
      progress: "bg-rose-300",
    };
  }

  if (
    p.includes("film") ||
    p.includes("static") ||
    p.includes("curl") ||
    p.includes("roller")
  ) {
    return {
      border: "hover:border-fuchsia-300/40",
      chip: "border-fuchsia-300/20 bg-fuchsia-400/10 text-fuchsia-300",
      iconWrap: "border-fuchsia-300/20 bg-fuchsia-400/10",
      icon: "text-fuchsia-300",
      glow: "hover:shadow-[0_0_24px_rgba(232,121,249,0.10)]",
      progress: "bg-fuchsia-300",
    };
  }

  if (
    p.includes("press") ||
    p.includes("scorch") ||
    p.includes("temp") ||
    p.includes("pressure") ||
    p.includes("cold peel") ||
    p.includes("hot peel")
  ) {
    return {
      border: "hover:border-amber-300/40",
      chip: "border-amber-300/20 bg-amber-400/10 text-amber-300",
      iconWrap: "border-amber-300/20 bg-amber-400/10",
      icon: "text-amber-300",
      glow: "hover:shadow-[0_0_24px_rgba(252,211,77,0.10)]",
      progress: "bg-amber-300",
    };
  }

  return {
    border: "hover:border-cyan-300/40",
    chip: "border-cyan-300/20 bg-cyan-400/10 text-cyan-300",
    iconWrap: "border-cyan-300/20 bg-cyan-400/10",
    icon: "text-cyan-300",
    glow: "hover:shadow-[0_0_24px_rgba(34,211,238,0.10)]",
    progress: "bg-cyan-300",
  };
}

function inferLabel(problem: string) {
  const p = problem.toLowerCase();

  if (
    p.includes("white") ||
    p.includes("settling") ||
    p.includes("circulation")
  ) {
    return "White Ink";
  }

  if (
    p.includes("powder") ||
    p.includes("peel") ||
    p.includes("bond") ||
    p.includes("lift") ||
    p.includes("wash")
  ) {
    return "Adhesion";
  }

  if (
    p.includes("head") ||
    p.includes("damper") ||
    p.includes("pump") ||
    p.includes("encoder") ||
    p.includes("carriage") ||
    p.includes("strike")
  ) {
    return "Hardware";
  }

  if (
    p.includes("film") ||
    p.includes("static") ||
    p.includes("curl") ||
    p.includes("roller")
  ) {
    return "Film";
  }

  if (
    p.includes("press") ||
    p.includes("scorch") ||
    p.includes("temp") ||
    p.includes("pressure")
  ) {
    return "Heat Press";
  }

  if (
    p.includes("humidity") ||
    p.includes("temperature") ||
    p.includes("dust")
  ) {
    return "Environment";
  }

  return "Print Quality";
}

function getSeverityClasses(severity?: string) {
  switch (severity) {
    case "critical":
      return "border-red-400/20 bg-red-500/10 text-red-300";
    case "high":
      return "border-orange-400/20 bg-orange-500/10 text-orange-300";
    case "medium":
      return "border-amber-400/20 bg-amber-500/10 text-amber-300";
    case "low":
      return "border-emerald-400/20 bg-emerald-500/10 text-emerald-300";
    default:
      return "border-white/10 bg-white/5 text-white/55";
  }
}

function getFrontHint(problem: string) {
  const p = problem.toLowerCase();

  if (p.includes("banding")) {
    return "Often tied to nozzles, alignment, or feed consistency.";
  }

  if (p.includes("white")) {
    return "Usually linked to white flow, settling, or nozzle health.";
  }

  if (p.includes("powder") || p.includes("peel") || p.includes("bond")) {
    return "Usually tied to cure quality, timing, or press setup.";
  }

  if (p.includes("head") || p.includes("damper") || p.includes("pump")) {
    return "Usually points to maintenance, flow, or mechanical hardware issues.";
  }

  if (p.includes("film") || p.includes("static")) {
    return "Often connected to media quality, storage, or environment.";
  }

  if (p.includes("press") || p.includes("temp") || p.includes("pressure")) {
    return "Usually points to application settings or platen inconsistency.";
  }

  return "Tap to reveal likely causes and the fastest next fix.";
}

export function FlipCard({
  card,
  cardNumber,
  isBookmarked,
  onToggleBookmark,
}: FlipCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);

  const causes = useMemo(() => normalizeToArray(card.cause), [card.cause]);
  const fixes = useMemo(() => normalizeToArray(card.fix), [card.fix]);

  const theme = getAccentClasses(card.problem);
  const label = card.category || inferLabel(card.problem);
  const severity = card.severity || "medium";
  const hint = getFrontHint(card.problem);

  return (
    <div
      className={`group relative min-h-[420px] overflow-hidden rounded-[24px] border border-white/10 bg-gradient-to-br from-[#091224] via-[#0b1530] to-[#080d18] transition duration-300 hover:-translate-y-1 ${theme.border} ${theme.glow}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.10),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(217,70,239,0.06),transparent_30%)]" />

      <button
        type="button"
        onClick={() => setIsFlipped((prev) => !prev)}
        className="absolute inset-0 z-10 rounded-[24px]"
        aria-label={
          isFlipped ? `Show front of ${card.problem}` : `Flip ${card.problem}`
        }
      />

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleBookmark();
        }}
        className={`absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-2xl border transition ${
          isBookmarked
            ? "border-cyan-300/25 bg-cyan-400/10 text-cyan-300"
            : "border-white/10 bg-white/5 text-white/60 hover:border-cyan-300/20 hover:bg-white/10 hover:text-white"
        }`}
        aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
      >
        <Bookmark
          className={`h-5 w-5 ${isBookmarked ? "fill-current" : ""}`}
        />
      </button>

      {!isFlipped ? (
        <div className="relative z-0 flex h-full flex-col p-5">
          <div className="mb-4 flex items-start justify-between gap-3 pr-14">
            <div className="text-sm font-semibold text-white/45">
              #{cardNumber ?? card.id}
            </div>

            <div
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.chip}`}
            >
              {label}
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <div
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${getSeverityClasses(
                severity
              )}`}
            >
              {severity}
            </div>

            <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/55">
              {causes.length} cause{causes.length !== 1 ? "s" : ""}
            </div>
          </div>

          <div className="flex-1">
            <div
              className={`mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border ${theme.iconWrap}`}
            >
              <Sparkles className={`h-7 w-7 ${theme.icon}`} />
            </div>

            <h3 className="text-[2rem] font-black leading-tight tracking-tight text-white">
              {card.problem}
            </h3>

            <p className="mt-4 text-sm leading-7 text-white/68">{hint}</p>

            <div className="mt-5 rounded-2xl border border-white/10 bg-black/15 p-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-300">
                Quick Preview
              </div>

              <div className="flex items-start gap-3 text-sm leading-6 text-white/72">
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-cyan-300" />
                <span>
                  Tap to inspect likely causes and the fastest fix path.
                </span>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="mb-4 h-px bg-white/10" />

            <div className="flex items-center justify-between text-sm text-white/55">
              <span>Tap to flip</span>
              <RotateCcw className="h-4 w-4" />
            </div>
          </div>
        </div>
      ) : (
        <div className="relative z-0 flex h-full flex-col p-5">
          <div className="mb-4 flex items-start justify-between gap-3 pr-14">
            <div className="text-sm font-semibold text-white/45">
              #{cardNumber ?? card.id}
            </div>

            <div
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${theme.chip}`}
            >
              {label}
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <div
              className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${getSeverityClasses(
                severity
              )}`}
            >
              {severity}
            </div>

            <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/55">
              Action View
            </div>
          </div>

          <h3 className="pr-2 text-xl font-extrabold leading-tight tracking-tight text-white">
            {card.problem}
          </h3>

          <div className="mt-5 flex-1 space-y-4 overflow-hidden">
            <div className="rounded-[20px] border border-white/10 bg-black/15 p-4">
              <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-fuchsia-200">
                <AlertTriangle className="h-4 w-4" />
                Likely Cause
              </div>

              <div className="space-y-2">
                {causes.slice(0, 3).map((item, index) => (
                  <div
                    key={`cause-${index}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm leading-6 text-white/72"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[20px] border border-cyan-300/15 bg-cyan-400/[0.04] p-4">
              <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
                <Wrench className="h-4 w-4" />
                Fast Fix
              </div>

              <div className="space-y-2">
                {fixes.slice(0, 3).map((item, index) => (
                  <div
                    key={`fix-${index}`}
                    className="rounded-2xl border border-cyan-300/15 bg-black/15 px-3 py-2.5 text-sm leading-6 text-white/78"
                  >
                    <span className="mr-2 font-semibold text-cyan-300">
                      {index + 1}.
                    </span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="mb-4 h-px bg-white/10" />

            <div className="flex items-center justify-between text-sm text-white/55">
              <span>Tap to flip back</span>
              <RotateCcw className="h-4 w-4" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}